import cornerstoneTools from "cornerstone-tools";

import drawRect from "./drawing/rectUtils";
const BaseAnnotationTool = cornerstoneTools.importInternal(
  "base/BaseAnnotationTool"
);

const external = cornerstoneTools.external;

const draw = cornerstoneTools.importInternal("drawing/draw");
const drawCircle = cornerstoneTools.importInternal("drawing/drawCircle");
const drawLine = cornerstoneTools.importInternal("drawing/drawLine");
// const drawRect = cornerstoneTools.importInternal('drawing/drawRect');
const setShadow = cornerstoneTools.importInternal("drawing/setShadow");
const getNewContext = cornerstoneTools.importInternal("drawing/getNewContext");

const getToolState = cornerstoneTools.getToolState;
//const toolStyle = cornerstoneTools.toolStyle
const getModule = cornerstoneTools.getModule;

const calculateSUV = cornerstoneTools.importInternal("util/calculateSUV");
const getPixelSpacing = cornerstoneTools.importInternal("util/getPixelSpacing");
const throttle = cornerstoneTools.importInternal("util/throttle");
const rectangleRoiCursor = cornerstoneTools.importInternal("cursors/index");
//const numbersWithCommas = cornerstoneTools.importInternal('util/numbersWithCommas');

/**
 * @public
 * @class RectangleStatic
 * @memberof Tools.Annotation
 * @classdesc Tool for drawing rectangular regions of interest, and measuring
 * the statistics of the enclosed pixels.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class AiSegmentationTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "AiSegmentation",
      supportedInteractionTypes: [],
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        // showMinMax: false,
        // showHounsfieldUnits: true
      },
      svgCursor: rectangleRoiCursor,
    };

    super(props, defaultProps);

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 110);
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      return;
    }

    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
        initialRotation: eventData.viewport.rotation,
      },
    };
  }

  renderToolData(evt) {
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    const eventData = evt.detail;
    const { element, canvasContext } = eventData;
    const context = getNewContext(canvasContext.canvas);
    const { pointSize } = this.configuration;

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false) {
        continue;
      }

      draw(context, (context) => {
        if (this.configuration.shadow) {
          setShadow(context, this.configuration);
        }

        // Get points directly from data or from nested structure
        const pointsArray = data.points || [];

        // Get color from data or default to red
        const color = data.color || "red";

        // Sort points based on x values
        const sortedPoints = [...pointsArray].sort((a, b) => a.x - b.x);

        // Draw each point using the sorted points array
        for (let j = 0; j < sortedPoints.length; j++) {
          const point = sortedPoints[j];
          const circleOptions = { color };

          // Draw Circle
          drawCircle(context, element, point, 1, circleOptions, "pixel");

          // Draw line to next point if distance > 2
          if (j < sortedPoints.length - 1) {
            const nextPoint = sortedPoints[j + 1];
            const dx = nextPoint.x - point.x;
            const dy = nextPoint.y - point.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Check if points are consecutive in the original array before drawing line
            let shouldDrawLine = true;

            // If we have original points and point status (from eraser tool)
            if (data.originalPoints && data.pointStatus) {
              // Find indices of these points in the original array
              const currentPointIndex = data.originalPoints.findIndex(
                (p) => p.x === point.x && p.y === point.y
              );
              const nextPointIndex = data.originalPoints.findIndex(
                (p) => p.x === nextPoint.x && p.y === nextPoint.y
              );

              // Check if these points were originally adjacent in the original array
              // Only draw lines between points that were originally adjacent
              if (currentPointIndex !== -1 && nextPointIndex !== -1) {
                // If points weren't originally adjacent (or within 1 position), don't draw line
                if (Math.abs(currentPointIndex - nextPointIndex) !== 1) {
                  shouldDrawLine = false;
                }

                // Check if the line between these specific points has been erased
                if (
                  data.erasedLines &&
                  data.erasedLines.has(`${currentPointIndex}-${nextPointIndex}`)
                ) {
                  shouldDrawLine = false;
                }

                // Also check for reverse order (in case points are sorted differently)
                if (
                  data.erasedLines &&
                  data.erasedLines.has(`${nextPointIndex}-${currentPointIndex}`)
                ) {
                  shouldDrawLine = false;
                }
              }
            } else {
              // For initial drawing (before any erasing), use distance threshold
              if (distance < 1) {
                shouldDrawLine = false;
              }
            }

            if (shouldDrawLine) {
              drawLine(context, element, point, nextPoint, { color }, "pixel");
            }
          }
        }

        // Draw label if it exists
        if (data.label) {
          // Find rightmost plotable point to place label
          if (sortedPoints.length > 0) {
            // Find the rightmost plotable point
            let rightmostPlotablePoint = null;
            let maxX = -Infinity;

            for (let j = 0; j < sortedPoints.length; j++) {
              const point = sortedPoints[j];
              try {
                // Check if the point can be converted to canvas coordinates
                const canvasPoint = external.cornerstone.pixelToCanvas(
                  element,
                  point
                );
                if (canvasPoint && point.x > maxX && point.y !== 0) {
                  maxX = point.x;
                  rightmostPlotablePoint = point;
                }
              } catch (error) {
                // Skip points that can't be converted to canvas coordinates
              }
            }

            // Only draw label if we found at least one plotable point
            if (rightmostPlotablePoint) {
              const canvasPoint = external.cornerstone.pixelToCanvas(
                element,
                rightmostPlotablePoint
              );

              context.font = "14px Arial";
              context.fontWeight = "bold";
              context.fillStyle = color;
              context.fillText(
                `${data.label}-AI`,
                canvasPoint.x - 45,
                canvasPoint.y - (data.label === "BM" ? -35 : 15)
              );
            }
          }
        }
      });
    }
  }
}
